import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { api } from '../api'
import { useMeta } from '../meta'
import type { HomeDetail } from '../types'
import { ago, staleness, when } from '../util'

/**
 * One household: who owns it, whether it is connected, what it pays, and every
 * thermostat in it.
 *
 * The thermostats are the point of the page — everything above them is the
 * context an operator needs before acting on one.
 */
export default function Home() {
  const { homeId = '' } = useParams()
  const nav = useNavigate()
  const { poll_interval_s: poll } = useMeta()

  const { data, error, isLoading } = useQuery<HomeDetail>({
    queryKey: ['home', homeId],
    queryFn: () => api<HomeDetail>(`/admin/homes/${homeId}`),
    refetchInterval: 60_000,
  })

  if (isLoading) return <p className="muted">Loading…</p>
  if (error) return <div className="err">{(error as Error).message}</div>
  if (!data) return null

  const h = data.home
  const live = data.devices.filter((d) => !d.removed)

  return (
    <>
      <div className="crumbs">
        <Link to="/homes">Homes</Link><span>/</span><span>{h.name}</span>
      </div>
      <h1>{h.name}</h1>
      <p className="sub">
        {h.vendor_structure_name && h.vendor_structure_name !== h.name
          && <>Google calls this “{h.vendor_structure_name}” · </>}
        {[h.city, h.state].filter(Boolean).join(', ')}
        {h.postal_code && ` · ${h.postal_code}`} · {h.timezone}
        {h.elevation_m != null && ` · ${Math.round(h.elevation_m)} m`}
      </p>

      <div className="stats">
        <div className="stat">
          <div className="lbl">Owner</div>
          <div className="v" style={{ fontSize: 15, wordBreak: 'break-all' }}>
            {h.owner_email}
          </div>
          <div className="sub2">since {when(h.owner_since)}</div>
        </div>
        <div className="stat">
          <div className="lbl">Connection</div>
          <div className="v" style={{ fontSize: 15 }}>
            {h.link_status === 'ok' ? 'Connected'
              : h.link_status === 'reauth_required' ? 'Needs reconnect'
                : h.link_status ?? 'Not linked'}
          </div>
          <div className="sub2">
            {h.vendor ?? '—'}{h.last_ok_at && ` · last ok ${ago(h.last_ok_at)}`}
          </div>
        </div>
        <div className="stat">
          <div className="lbl">Plan</div>
          <div className="v" style={{ fontSize: 15 }}>
            {h.entitlement_source
              ? h.entitlement_source === 'coupon' ? 'Comped' : 'Active'
              : h.subscription_status ?? 'None'}
          </div>
          <div className="sub2">
            {h.active_until ? `until ${when(h.active_until)}`
              : h.current_period_end ? `renews ${when(h.current_period_end)}`
                : 'no entitlement'}
          </div>
        </div>
        <div className="stat">
          <div className="lbl">Thermostats</div>
          <div className="v">{live.length}</div>
          <div className="sub2">
            {live.filter((d) => d.allow_control).length} allowing control
          </div>
        </div>
      </div>

      {h.last_error && <div className="err">{h.last_error}</div>}

      <Plant detail={data} />

      <h2 style={{ fontSize: 15, margin: '22px 0 12px' }}>Thermostats</h2>

      {data.devices.length === 0 ? (
        <div className="panel"><div className="empty">
          <h3>No thermostats</h3>
          <p>The home is created but nothing has been linked to it yet.</p>
        </div></div>
      ) : (
        <div className="cards">
          {data.devices.map((d) => {
            const s = staleness(d.reading_at, poll)
            const running = d.hvac && d.hvac !== 'OFF'
            return (
              <button key={d.id}
                      className={`card${d.removed ? ' dim' : ''}`}
                      onClick={() => nav(`/homes/${homeId}/t/${d.id}`)}>
                <div className="top">
                  <div>
                    <div className="name">{d.display_name}</div>
                    <div className="where">
                      {d.room_name ? <>{d.room_name} · </> : null}
                      {/* Mode is the fact nobody can infer: an idle zone in
                          COOL and an idle zone in HEAT look identical. */}
                      {d.mode ?? 'unknown mode'}
                      {d.eco && d.eco !== 'OFF' && ' · eco'}
                    </div>
                  </div>
                  {d.removed
                    ? <span className="chip">Removed</span>
                    : running
                      ? <span className="chip on">{d.hvac?.toLowerCase()}</span>
                      : <span className="chip bare">Idle</span>}
                </div>

                <div className="big">
                  {d.temp != null ? `${((d.temp * 9) / 5 + 32).toFixed(1)}°` : '—'}
                </div>

                <div className="facts">
                  <div className="fact">
                    <div className="k2">Setpoint</div>
                    <div className="v2">
                      {d.setpoint != null
                        ? `${((d.setpoint * 9) / 5 + 32).toFixed(0)}°` : '—'}
                    </div>
                  </div>
                  <div className="fact">
                    <div className="k2">Humidity</div>
                    <div className="v2">
                      {d.humidity != null ? `${d.humidity.toFixed(0)}%` : '—'}
                    </div>
                  </div>
                  <div className="fact">
                    <div className="k2">Control</div>
                    <div className="v2">
                      {d.allow_control
                        ? <span className="chip on">Allowed</span>
                        : <span className="muted">Shadow</span>}
                    </div>
                  </div>
                  <div className="fact">
                    <div className="k2">Last reading</div>
                    <div className={`v2 ${s.cls === 'danger' ? 'danger' : ''}`}>
                      {d.removed ? `${d.samples.toLocaleString()} archived` : s.label}
                    </div>
                  </div>
                </div>
              </button>
            )
          })}
        </div>
      )}
    </>
  )
}


const PLANT: Record<string, string> = {
  shared: 'One outdoor unit, shared',
  independent: 'Separate outdoor units',
  unknown: 'Unknown — treated as shared',
}

/**
 * The home's equipment, and who is currently allowed to use it.
 *
 * Only rendered for a home with more than one thermostat, because that is the
 * only case where the question exists. A shared-plant home where two zones
 * contended for the compressor is something an operator has to be able to see:
 * a zone reporting `arbiter_queued` on its own page looks like a fault until
 * you can see the neighbour that was using the unit.
 */
function Plant({ detail }: { detail: HomeDetail }) {
  const live = detail.devices.filter((d) => !d.removed)
  if (live.length < 2) return null

  const h = detail.home
  const a = detail.arbiter
  const name = (id: string | null) =>
    live.find((d) => d.id === id)?.display_name ?? (id ? id.slice(0, 8) : '—')

  return (
    <>
      <h2 style={{ fontSize: 15, margin: '22px 0 12px' }}>Cooling plant</h2>
      <div className="stats">
        <div className="stat">
          <div className="lbl">Topology</div>
          <div className="v" style={{ fontSize: 15 }}>
            {PLANT[h.hvac_topology] ?? h.hvac_topology}
          </div>
          <div className="sub2">
            {h.topology_source === 'default'
              ? 'nobody has answered yet'
              : `answered by the ${h.topology_source}`}
          </div>
        </div>
        <div className="stat">
          <div className="lbl">Compressor</div>
          <div className="v" style={{ fontSize: 15 }}>
            {a?.holder ? name(a.holder) : 'Free'}
          </div>
          <div className="sub2">
            {a?.holder_since
              ? `held since ${when(new Date(a.holder_since * 1000).toISOString())}`
              : 'no zone is using it'}
          </div>
        </div>
        <div className="stat">
          <div className="lbl">Waiting</div>
          <div className="v">{a?.waiting?.length ?? 0}</div>
          <div className="sub2">
            {a?.waiting?.length
              ? a.waiting.map(name).join(', ')
              : 'no queue'}
          </div>
        </div>
      </div>
    </>
  )
}
